import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Form from "../../../base/form";
import {MuiRFF, Yup} from '../../../form';
import DateUtils from "../../../../../core/services/date-utils";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import CustomerAutocomplete from "../../../text-fields/autocomplete/customer-autocomplete";
import ToggleButtons from "../../../base/button/toggle_buttons";
import OperatorAutocomplete from "../../../text-fields/autocomplete/operator-autocomplete";
import {Button, Card, Paper, Unstable_Grid2 as Grid} from "@mui/material";


export const LookupAnalyticsDateOptionsValues = {
    last24Hours: 'last24Hours',
    last7Days: 'last7Days',
    last2Weeks: 'last2Weeks',
    last30Days: 'last30Days',
}

export const LookupAnalyticsDateOptionRanges = {
    last24Hours: () => ({
        startDate: DateUtils.UTCNow().subtract(1, 'day'),
        endDate: DateUtils.UTCNow(),
    }),
    last7Days: () => ({
        startDate: DateUtils.UTCNow().subtract(7, 'day'),
        endDate: DateUtils.UTCNow(),
    }),
    last2Weeks: () => ({
        startDate: DateUtils.UTCNow().subtract(2, 'week'),
        endDate: DateUtils.UTCNow(),
    }),
    last30Days: () => ({
        startDate: DateUtils.UTCNow().subtract(30, 'day'),
        endDate: DateUtils.UTCNow(),
    }),
}

const LookupAnalyticsDashboardViewSearchSection = ({filters, setFilters}) => {
    const [dateOption, setDateOption] = useState();
    const minDate = useMemo(() => DateUtils.UTCNow().subtract(1, 'year'), []);

    /**@type {React.MutableRefObject<formApi>}*/
    const formApiRef = useRef();

    const schema = useMemo(() => Yup.object().shape({
        customer: Yup.mixed().nullable(),
        operator: Yup.mixed().nullable(),
        startDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
            .minDate(DateUtils.UTCNow().subtract(1, 'year'), ValidateMessages.greaterThan("1 year ago"))
            .maxDate(Yup.ref('endDate'), ValidateMessages.lessThan("End Date"))
            .nullable()
        ,
        endDate: Yup.mixed()
            .validDate(ValidateMessages.incorrect)
            .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
            .minDate(DateUtils.UTCNow().subtract(1, 'year'), ValidateMessages.greaterThan("1 year ago"))
            .minDate(Yup.ref("startDate"), ValidateMessages.greaterThan("Start Date"))
            .nullable(),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);


    const initialValues = useMemo(() => ({
        customer: filters?.customer,
        operator: filters?.operator,
        startDate: filters?.startDate ? DateUtils.serverToUTC(filters?.startDate) : LookupAnalyticsDateOptionRanges.last2Weeks().startDate,
        endDate: filters?.endDate ? DateUtils.serverToUTC(filters?.endDate) : LookupAnalyticsDateOptionRanges.last2Weeks().endDate,
    }), [filters]);

    /**
     * Sets the date option based on the filters
     */
    useEffect(() => {
        const period = {
            startDate: filters?.startDate ? DateUtils.serverToUTC(filters?.startDate) : LookupAnalyticsDateOptionRanges.last2Weeks().startDate,
            endDate: filters?.endDate ? DateUtils.serverToUTC(filters?.endDate) : LookupAnalyticsDateOptionRanges.last2Weeks().endDate,
        }
        const ranges = Object.entries(LookupAnalyticsDateOptionRanges).map(e => ({key: e[0], range: e[1]()}));
        const found = ranges.find(e => e.range.startDate.isSame(period.startDate, 'minute') && e.range.endDate.isSame(period.endDate, 'minute'));
        if (found) {
            setDateOption(LookupAnalyticsDateOptionsValues[found.key]);
        }
    }, [filters?.startDate, filters?.endDate])


    /**
     * Changes the query based on the added filters which triggers the search in responsible view
     * @param values
     */
    const onSubmit = (values) => {
        setFilters(prevState => ({
            ...prevState,
            customer: values.customer,
            operator: values.operator,
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        }))
    }

    /**
     * Changes the date input values based on the selected toggle button option
     */
    const changeDateByToggleButtons = useCallback((value) => {
        // refresh the same value if clicked again.
        if (!value) {
            value = dateOption;
        }
        setDateOption(value);
        let period = LookupAnalyticsDateOptionRanges[value]?.();
        if (!period)
            return;
        formApiRef.current?.change('startDate', period.startDate);
        formApiRef.current?.change('endDate', period.endDate);
    }, [dateOption])


    const predefinedDateOptions = useMemo(() => [
        {
            value: LookupAnalyticsDateOptionsValues.last24Hours,
            name: "Last 24 hours",
            disabled: false,
        },
        {
            value: LookupAnalyticsDateOptionsValues.last7Days,
            name: "Last 7 days",
            disabled: false,
        },
        {
            value: LookupAnalyticsDateOptionsValues.last2Weeks,
            name: "Last 2 weeks",
            disabled: false,
        },
        {
            value: LookupAnalyticsDateOptionsValues.last30Days,
            name: "Last 30 days",
            disabled: false,
        },
    ], [])

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{
                    marginBlock: 2,
                    padding: 2,
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Paper sx={{boxShadow: 'none', padding: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                    <Form
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        render={({values, form}) => {
                            formApiRef.current = form;
                            return (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid xs={12} sx={{display: 'flex', justifyContent: 'start'}}>
                                            <ToggleButtons
                                                value={dateOption}
                                                onChange={(_, v) => changeDateByToggleButtons(v)}
                                                options={predefinedDateOptions}
                                                size={'small'}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6}>
                                            <MuiRFF.DateTimePicker
                                                name={'startDate'}
                                                label={"Start Date"}
                                                required={required.startDate}
                                                maxDate={values.endDate}
                                                format="YYYY-MM-DD HH:mm"
                                                disableFuture
                                                minDate={minDate}
                                                onChange={(e) => setDateOption(null) || form.change('startDate', e)}
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6}>
                                            <MuiRFF.DateTimePicker
                                                name={'endDate'}
                                                label={"End Date"}
                                                required={required.endDate}
                                                minDate={values.startDate ?? minDate}
                                                format="YYYY-MM-DD HH:mm"
                                                disableFuture
                                                onChange={(e) => setDateOption(null) || form.change('endDate', e)}
                                                size={'small'}
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} padding={0}/>
                                        <Grid xs={12} sm={6} lg>
                                            <CustomerAutocomplete
                                                form
                                                name={'customer'}
                                                label={'Customer'}
                                                placeholder={'Search for customer...'}
                                                fullWidth
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} sm={6} lg>
                                            <OperatorAutocomplete
                                                form
                                                name={'operator'}
                                                label={'Operator'}
                                                placeholder={'Search for operator...'}
                                                fullWidth
                                                textFieldProps={{
                                                    size: 'small'
                                                }}
                                            />
                                        </Grid>
                                        <Grid xs={12} lg={'auto'} sx={{display: 'flex', justifyContent: {xs: 'center', lg: 'end'}}}>
                                            <Button
                                                variant={'contained'}
                                                color={'primary'}
                                                type={'submit'}
                                                size={'medium'}
                                                sx={{paddingBlock: 0.5, paddingInline: 2, minWidth: 190, display: 'flex'}}
                                            >
                                                Search
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </>
                            );
                        }}
                    />
                </Paper>
            </Card>
        </>
    );
}

export default LookupAnalyticsDashboardViewSearchSection;
